// The lesson format. Authored blocks are imported into it; "teach me X" lessons are generated into it.
// The app renders every lesson from this structure; nothing else reaches the lesson player.
import { z } from 'zod';

const Id = z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/);

/** Restricted inline HTML: b, strong, i, em, var, sub, sup, code, a[href], br, span.math. Sanitized server-side before storage. */
export const Rich = z.string().max(20_000);

/** A concept the lesson teaches. Ids are global across courses (e.g. "attention.softmax-shares"). */
export const Concept = z.object({ id: z.string().regex(/^[a-z0-9.-]{1,80}$/), label: z.string().max(120) });
/** A relationship the lesson teaches between two concepts; the learner model tracks evidence for links separately. */
export const Link = z.object({ from: Concept.shape.id, to: Concept.shape.id, label: z.string().max(160) });

export const CheckpointKind = z.enum(['sketch', 'predict', 'bug', 'redraw']);
export const Checkpoint = z.object({
  id: Id,
  kind: CheckpointKind,
  title: z.string().max(80),
  /** Shown to the learner above the paper. */
  prompt: z.string().max(400),
  /** Image drawn under the learner's ink (bug checkpoints); path relative to /content/assets/. */
  background: z.string().nullable(),
  /** Plain-language statements of what has been taught by this point; the tutor may only ask about these. */
  taught: z.array(z.string().max(400)).min(1),
  /** Concept ids and link ids ("from>to") this checkpoint produces evidence for. */
  concepts: z.array(z.string().regex(/^[a-z0-9.-]{1,80}(>[a-z0-9.-]{1,80})?$/)),
  /** Tutor-only: what a complete answer contains. Never example numbers to recall. */
  reference: z.string().max(4000),
  /** Tutor-only: wrong ideas to probe for. */
  misconceptions: z.array(z.string().max(400)),
});

/** A script-driven embed from the course kit (films and interactive figures). */
export const Embed = z.object({
  /** Script URLs under /course/, loaded in order (kit, course runtime, voice manifest, the film module). */
  scripts: z.array(z.string().startsWith('/course/')),
  /** Stylesheets under /course/ the embed needs. */
  styles: z.array(z.string().startsWith('/course/')),
  /** Global function called with (hostElement, options), e.g. "MT.attention". Ignored when `frame` is set. */
  call: z.string().regex(/^[A-Za-z_$][\w$]*(\.[A-Za-z_$][\w$]*)*$/),
  options: z.record(z.string(), z.unknown()),
  /** Act titles in order (films with beats); empty for figures. */
  acts: z.array(z.string()),
  /**
   * Self-initialising widgets (they find their host by id when their scripts load): the server builds a minimal page
   * from `page` (its stylesheets, the element with `hostId`, its scripts in order) at
   * GET /embed-frame?page=…&host=…, and the renderer shows it in a same-origin iframe sized to its content.
   * `scripts`/`styles`/`call`/`options` are then informational only.
   */
  frame: z.object({ page: z.string().regex(/^\/course\/[\w./-]+\.html$/), hostId: z.string().regex(/^[A-Za-z][\w-]{0,80}$/) }).nullable().optional(),
});

export const Section = z.discriminatedUnion('kind', [
  /** One idea. Renderers may show one text section per screen. A titled section may be a bare heading over the boxes that follow it. */
  z.object({ kind: z.literal('text'), id: Id, title: z.string().max(120).nullable(), paragraphs: z.array(Rich) }).refine(s => s.title !== null || s.paragraphs.length > 0, 'An untitled text section needs a paragraph'),
  z.object({ kind: z.literal('embed'), id: Id, label: z.string().max(80), caption: Rich, embed: Embed }),
  /** Mermaid source rendered with Pele (links and images disabled). */
  z.object({ kind: z.literal('diagram'), id: Id, caption: Rich, mermaid: z.string().max(20_000) }),
  /** "You might think": a tempting wrong idea and why it's wrong. */
  z.object({ kind: z.literal('think'), id: Id, claim: Rich, answer: z.array(Rich).min(1) }),
  /** A guess the course page asks inline (multiple choice), answerable from what came before it. */
  z.object({ kind: z.literal('guess'), id: Id, question: Rich, options: z.array(Rich).min(2), answer: z.number().int().min(0), explanation: Rich }),
  /** Optional depth: maths, sources. Collapsed by default. */
  z.object({ kind: z.literal('deep'), id: Id, title: z.string().max(160), body: z.array(Rich).min(1) }),
  /** Source code shown verbatim (monospace, no execution). */
  z.object({ kind: z.literal('code'), id: Id, language: z.string().max(20), code: z.string().max(40_000), caption: Rich.nullable() }),
  /** Authored static figure (inline SVG/HTML from an imported course page, allowlist-sanitized at import). Never produced by generation. */
  z.object({ kind: z.literal('figure'), id: Id, html: z.string().max(400_000), caption: Rich }),
  /**
   * A live, same-origin explainer player; never a rendered video. `top` films open the lesson (before its first section)
   * and narrate the lesson as a whole: they never gate checkpoints, which unlock by scrolling or by the film's own stops.
   */
  z.object({
    kind: z.literal('film'), id: Id, title: z.string().max(160), caption: Rich,
    film: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/), aspect: z.literal('16:9').optional(), top: z.literal(true).optional(),
  }),
  /**
   * A rendered film (Manim + narration). Files live under /content/media/ (served with HTTP range support).
   * `chapters` play the role of an embed's acts: a following checkpoint's afterAct indexes into them.
   */
  z.object({
    kind: z.literal('video'), id: Id, title: z.string().max(160), caption: Rich,
    src: z.string().regex(/^\/content\/media\/[\w./-]+\.mp4$/),
    poster: z.string().regex(/^\/content\/media\/[\w./-]+\.(png|jpg|webp)$/).nullable(),
    /** WebVTT captions, one per language the narration exists in. */
    captions: z.array(z.object({ lang: z.enum(['en', 'it']), src: z.string().regex(/^\/content\/media\/[\w./-]+\.vtt$/) })),
    chapters: z.array(z.object({ title: z.string().max(120), start: z.number().min(0) })),
    durationSec: z.number().positive(),
  }),
  /** A pen exercise placed here. It unlocks when the learner reaches this point (or the given act of the preceding embed). */
  z.object({ kind: z.literal('checkpoint'), id: Id, afterAct: z.number().int().min(0).nullable(), checkpoint: Checkpoint }),
]);

export const Lesson = z.object({
  id: Id,
  courseId: Id,
  /** Language of the lesson text; checkpoints and the tutor's replies use it too. */
  language: z.enum(['en', 'it']).default('en'),
  title: z.string().max(160),
  /** e.g. "Block I.3 · Part I · Attention". */
  kicker: z.string().max(160),
  minutes: z.number().int().min(1).max(120),
  /** One line: what this lesson lets you do (the organising idea). */
  problem: Rich,
  takeaway: Rich,
  sections: z.array(Section).min(1),
  concepts: z.array(Concept),
  links: z.array(Link),
  source: z.discriminatedUnion('type', [
    z.object({ type: z.literal('imported'), url: z.string(), importedAt: z.string() }),
    z.object({ type: z.literal('generated'), prompt: z.string(), model: z.string(), generatedAt: z.string(), ownerId: z.string() }),
  ]),
});

export const Course = z.object({
  id: Id,
  title: z.string().max(160),
  summary: Rich,
  /** Lesson ids in order, grouped into parts. */
  parts: z.array(z.object({ title: z.string().max(120), lessons: z.array(Id) })),
});

export type Rich = z.infer<typeof Rich>;
export type Concept = z.infer<typeof Concept>;
export type Link = z.infer<typeof Link>;
export type Checkpoint = z.infer<typeof Checkpoint>;
export type Embed = z.infer<typeof Embed>;
export type Section = z.infer<typeof Section>;
export type Lesson = z.infer<typeof Lesson>;
export type Course = z.infer<typeof Course>;
